
import React from "react";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
} from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import {
  LogOut,
  LayoutDashboard,
  Users,
  FileText,
  HelpCircle,
  File,
  UserPlus,
  Image,
  Settings,
  BookOpen
} from "lucide-react";
import { Link } from "react-router-dom";
import { User as SupabaseUser } from "@supabase/supabase-js";

interface AdminSidebarProps {
  activeModule: string;
  setActiveModule: (module: string) => void;
  user: SupabaseUser | null;
  handleLogout: () => Promise<void>;
}

const AdminSidebar: React.FC<AdminSidebarProps> = ({
  activeModule,
  setActiveModule,
  user,
  handleLogout
}) => {
  return (
    <Sidebar>
      <SidebarHeader className="border-b border-gray-200 p-4">
        <div className="flex justify-center">
          <img 
            src="https://toctoc.com.br/logo.png" 
            alt="Toc Toc" 
            className="h-8"
            onError={(e) => {
              // Fallback if logo doesn't load
              const target = e.target as HTMLImageElement;
              target.src = "https://via.placeholder.com/200x40?text=Toc+Toc";
            }} 
          />
        </div>
      </SidebarHeader>
      <SidebarContent className="py-4">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton 
              onClick={() => setActiveModule("dashboard")}
              isActive={activeModule === "dashboard"}
              className={activeModule === "dashboard" ? "bg-orange-50 text-[#FF5605]" : ""}
            >
              <LayoutDashboard className="w-5 h-5 mr-3" />
              <span>Home</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton 
              onClick={() => setActiveModule("users")}
              isActive={activeModule === "users"}
              className={activeModule === "users" ? "bg-orange-50 text-[#FF5605]" : ""}
            >
              <Users className="w-5 h-5 mr-3" />
              <span>Usuários</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton 
              onClick={() => setActiveModule("static-text")}
              isActive={activeModule === "static-text"}
              className={activeModule === "static-text" ? "bg-orange-50 text-[#FF5605]" : ""}
            >
              <FileText className="w-5 h-5 mr-3" />
              <span>Textos Fixos</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton 
              onClick={() => setActiveModule("faq")}
              isActive={activeModule === "faq"}
              className={activeModule === "faq" ? "bg-orange-50 text-[#FF5605]" : ""}
            >
              <HelpCircle className="w-5 h-5 mr-3" />
              <span>FAQ</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton 
              onClick={() => setActiveModule("pages")}
              isActive={activeModule === "pages"}
              className={activeModule === "pages" ? "bg-orange-50 text-[#FF5605]" : ""}
            >
              <File className="w-5 h-5 mr-3" />
              <span>Páginas Institucionais</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton 
              onClick={() => setActiveModule("images")}
              isActive={activeModule === "images"}
              className={activeModule === "images" ? "bg-orange-50 text-[#FF5605]" : ""}
            >
              <Image className="w-5 h-5 mr-3" />
              <span>Imagens</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton 
              onClick={() => setActiveModule("blog")}
              isActive={activeModule === "blog"}
              className={activeModule === "blog" ? "bg-orange-50 text-[#FF5605]" : ""}
            >
              <BookOpen className="w-5 h-5 mr-3" />
              <span>Blog</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton
              onClick={() => setActiveModule("leads")}
              isActive={activeModule === "leads"}
              className={activeModule === "leads" ? "bg-orange-50 text-[#FF5605]" : ""}
            >
              <UserPlus className="w-5 h-5 mr-3" />
              <span>Leads</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton asChild>
              <Link to="/admin/setup" className="flex items-center">
                <Settings className="w-5 h-5 mr-3" />
                <span>Setup</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarContent>
      <SidebarFooter className="border-t border-gray-200 p-4">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-full bg-[#3332FC] flex items-center justify-center text-white text-sm">
              {user?.email?.charAt(0).toUpperCase() || 'A'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.email}</p>
              <p className="text-xs text-gray-500">Admin</p>
            </div>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleLogout}
            className="w-full justify-start"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sair
          </Button>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
};

export default AdminSidebar;
